import { useContext, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import {
  DELETE_BUDGET,
  GET_BUDGET_TEMPLATES,
  UPDATE_BUDGET,
} from '@atom/graph/budget';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Snackbar } from '@atom/mui';
import { ArrowBackIcon, DeleteIcon, EditIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { BudgetModal, BudgetUpdateInput } from '@atom/types/budget';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { BudgetTemplateType } from '@atom/utilities/endpoints';
import { isRestrictedBudgetRolesEnabled } from '@atom/utilities/tenantFeatureUtilities';

import BudgetsFormModal from '../budgets/budgetsModal/BudgetsFormModal';
import DeleteModal from '../common/DeleteModal';

import BudgetUploadModal from './budgetUploadModal/BudgetUploadModal';
import BudgetDetailBudgetNav from './BudgetDetailBudgetNav';
import BudgetDetailContext from './BudgetDetailContext';
import { BUDGET_OPTIONS_LIMIT, getDateRange } from './budgetDetailUtils';
import BudgetExportMenu from './BudgetExportMenu';
import BudgetUploadMenu from './BudgetUploadMenu';
import { useBudgetBulkEditAccess } from './useBudgetBulkEditAccess';
import {
  deleteBudgetFilters,
  useBudgetFilterStorage,
} from './useBudgetFilterStorage';

import classes from '../../styles/detail-header.module.css';

const BudgetDetailHeader = () => {
  const [, { navigate, backDestination }] = useNavigation();
  const {
    budget,
    budgetOptions,
    totalBudgetOptions,
    loadingBudgetOptions,
    getBudgetOptions,
    parentBudgetUnit,
  } = useContext(BudgetDetailContext);
  const { saveFilterState } = useBudgetFilterStorage();
  const { canUpload } = useBudgetBulkEditAccess(
    parentBudgetUnit?.actionsOnBudgetItems,
  );

  const [activeModal, setActiveModal] = useState<BudgetModal>(null);
  const [page, setPage] = useState<number>(1);
  const [type, setType] = useState<BudgetTemplateType>('budgetItems');

  /*
    TODO: Replace temporary permissions here with RBAC
    NOTE: This is shared behavior with the BudgetsTable component
    
    Current Rules:
    QA and Production:
    - Create button is hidden for everyone except Admin for all budgets
    Dev and UAT:
    - Create button is hidden for everyone below Org Admin (show for Admin and Org Admin)
  
    source: https://atomai.atlassian.net/browse/AM-15294
  */
  const roleNeededToEditOrDelete: string[] = isRestrictedBudgetRolesEnabled
    ? ROLE_SETS.ADMIN
    : ROLE_SETS.ORG_ADMIN;

  const canEditBudgets: boolean = hasRolePermissions(roleNeededToEditOrDelete);
  const canDeleteBudgets: boolean = hasRolePermissions(
    roleNeededToEditOrDelete,
  );

  const handlePageScroll = (nextPage: number) => {
    setPage(nextPage);
    getBudgetOptions({
      variables: {
        input: {
          templateId: budget.templateId,
          page: nextPage,
          limit: BUDGET_OPTIONS_LIMIT,
          sortBy: 'name,asc',
        },
      },
    });
  };

  const budgetNamesUsed = useMemo(
    () => new Set(budgetOptions?.map(({ name }) => name) || []),
    [budgetOptions],
  );

  const { data: templatesData, loading: loadingTemplates } =
    useQuery(GET_BUDGET_TEMPLATES);

  const templates = R.pathOr(
    [],
    ['budgetTemplates', 'budgetTemplates'],
    templatesData,
  );

  const [deleteBudget, { loading: loadingDelete }] = useMutation(DELETE_BUDGET);

  const handleDeleteBudget = async () => {
    try {
      await deleteBudget({ variables: { id: budget.id } });
      deleteBudgetFilters(budget.id);
      Snackbar.info({
        message: `Deleted budget "${budget.name}"`,
      });
      navigate('/budgets');
    } catch {
      Snackbar.error({
        message: `An error occurred while deleting ${budget.name}. Please Try Again.`,
      });
    } finally {
      setActiveModal(null);
    }
  };

  const [updateBudget, { loading: loadingUpdate }] = useMutation(UPDATE_BUDGET);

  const handleUpdateBudget = async (input: BudgetUpdateInput) => {
    try {
      await updateBudget({
        variables: {
          input,
        },
      });
      Snackbar.info({
        message: `Updated budget "${input.name}"`,
      });
    } catch (error) {
      Snackbar.error({
        message:
          'An error occurred while updating your budget. Please Try Again.',
      });
    } finally {
      setActiveModal(null);
    }
  };

  const title: string = !R.isEmpty(budget)
    ? `${budget.name}${getDateRange(budget)}`
    : '';

  const handleGoBack = () => {
    saveFilterState();
    navigate(backDestination('/budgets'));
  };

  return (
    <div className={clsx(classes.headerContainer, classes.budgetsHeader)}>
      <div
        className={clsx(classes.nameContainer, classes.budgetsNameContainer)}
      >
        <IconButton onClick={handleGoBack} tooltip="Go back">
          <Icon color={colors.neutral.white} icon={ArrowBackIcon} />
        </IconButton>
        <span>{title}</span>
        <BudgetDetailBudgetNav
          options={budgetOptions}
          total={totalBudgetOptions}
          loading={loadingBudgetOptions}
          page={page}
          handlePageScroll={handlePageScroll}
        />
      </div>
      <div>
        <BudgetExportMenu />
        {canUpload && (
          <BudgetUploadMenu setType={setType} setActiveModal={setActiveModal} />
        )}
        {canEditBudgets && (
          <IconButton
            onClick={() => setActiveModal(BudgetModal.EDIT)}
            tooltip="Edit Details"
          >
            <Icon color={colors.neutral.white} icon={EditIcon} />
          </IconButton>
        )}
        {canDeleteBudgets && (
          <IconButton
            onClick={() => setActiveModal(BudgetModal.DELETE)}
            tooltip="Delete Budget"
          >
            <Icon color={colors.neutral.white} icon={DeleteIcon} />
          </IconButton>
        )}
        {activeModal === BudgetModal.EDIT && !loadingTemplates && (
          <BudgetsFormModal
            onClose={() => setActiveModal(null)}
            budget={budget}
            modalType={activeModal}
            templates={templates}
            budgetNamesUsed={budgetNamesUsed}
            handleUpdateBudget={handleUpdateBudget}
            saving={loadingUpdate}
          />
        )}
        {activeModal === BudgetModal.BULK_EDIT && (
          <BudgetUploadModal type={type} onClose={() => setActiveModal(null)} />
        )}
        <DeleteModal
          open={activeModal === BudgetModal.DELETE}
          title="Delete Budget?"
          content={`Are you sure you want to delete budget "${budget?.name}"?`}
          onCancel={() => setActiveModal(null)}
          onConfirm={handleDeleteBudget}
          disabled={loadingDelete}
          loading={loadingDelete}
        />
      </div>
    </div>
  );
};

export default BudgetDetailHeader;
